import type {LoaderFunction, MetaFunction} from '@remix-run/node'
import {json} from '@remix-run/node'
import {useRouteLoaderData} from '@remix-run/react'
import {getDomainConfig} from '~/domains'

import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {BrgIconContact} from '~/icons/contact-page/brg-icon-contact'
import {getTranslation} from '~/localization/translation'
import type {ContactLoaderResponse, RootLoaderResponse} from '~/types'
import {setEnvironmentWith} from '~/utils/async-storage.server'

export const meta: MetaFunction<typeof loader> = ({data}: {data: ContactLoaderResponse}) => {
  const locale = data?.locale?.toLowerCase() ?? 'us'
  const language = data?.language ?? 'en-US'
  const translations = getTranslation(language)
  const domain = data?.domain
  const {siteName} = getDomainConfig(domain, locale)
  const mainTitle = translations.Contact.Page.ogTitle(siteName)
  const titleContent = translations.Contact.Page.title(siteName)
  const description = translations.Contact.Page.description(siteName)

  return [
    {
      title: mainTitle,
    },
    {
      name: 'description',
      content: description,
    },
    {
      name: 'title',
      content: titleContent,
    },
    {
      name: 'viewport',
      content: 'width=device-width,initial-scale=1',
    },
    {
      name: 'charset',
      content: 'utf-8',
    },
    {
      property: 'og:url',
      content: `https://${domain}/contact`,
    },
    {
      tagName: 'link',
      rel: 'canonical',
      href: `https://${domain}/contact`,
    },

    {
      property: 'og:locale',
      content: locale,
    },
    {
      property: 'og:site_name',
      content: siteName,
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '1024',
    },
    {
      property: 'og:image:alt',
      content: `${siteName} Image`,
    },
    {
      property: 'og:title',
      content: titleContent,
    },
    {
      property: 'og:description',
      content: description,
    },
    {
      property: 'og:type',
      content: 'website',
    },
  ]
}

export const loader: LoaderFunction = async ({context}) => {
  setEnvironmentWith(context)
  const {locale, domain, language} = context

  return json({
    locale: locale.toUpperCase(),
    domain,
    language,
  })
}

export default function Contact() {
  const {language} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const reportClick = useReportWidgetClick()

  const sellerLink = 'https://roundforest.com/sellers/?utm_source=BRG&utm_medium=web&utm_campaign=popup'
  const contactEmail = 'contact@roundforest.com'

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'contact-page',
    widget_place: 'main',
  })

  return (
    <div
      ref={widgetRef}
      data-testid="contact-page"
      className="content-container mx-auto my-auto flex h-full flex-row justify-between py-5 tablet:flex-col mobile:mt-8 mobile:px-8"
    >
      <div className="h-full w-full">
        <div className="flex flex-col gap-10 py-10 tablet:gap-6">
          <p className="text-8xl font-semibold tablet:text-5xl">{translations.Contact.header}</p>
          <p className="text-md font-normal leading-5 desktop:pr-20">
            {translations.Contact.content}&nbsp;
            <a
              href={sellerLink}
              className="cursor-pointer underline"
              aria-label="contact seller link"
              onClick={() => {
                reportClick({widget_name: 'contact-page-seller-link', widget_place: 'main'})
              }}
            >
              {translations.Contact.seller}.
            </a>
          </p>
          <a
            href={`mailto:${contactEmail}`}
            className="w-fit cursor-pointer rounded-3xl border border-black px-4 py-2 tracking-wide"
            aria-label="contact email button contact@roundforest.com"
            onClick={() => {
              reportClick({widget_name: 'contact-page-email-button', widget_place: 'main'})
            }}
          >
            {contactEmail}
          </a>
        </div>
      </div>
      <div className="border-l border-l-black bg-transparent tablet:border-t tablet:border-t-black" />
      <div className="my-auto flex h-full w-full items-center justify-center tablet:mt-10">
        <BrgIconContact />
      </div>
    </div>
  )
}
